import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Icon, TooltipProvider, cn } from "@nexus/ui";
import {
  SCORE_BAND_NEEDS_REVIEW_MIN,
  SCORE_BAND_STRONG_MIN,
  actionableCeiling,
  projectScore,
} from "@nexus/contracts";
import type { CriterionAttribution } from "@nexus/contracts";

import type {
  PreflightReportDetail,
  PreflightScoreBand,
} from "../../types/preflight.types";
import { TONE_BG, TONE_TEXT } from "../../constants/result-state";
import {
  computeScoreAttribution,
  hasScoreComposition,
} from "../../lib/report-attribution";
import { useCountUp } from "../AnimatedNumber/AnimatedNumber";
import { BandPill } from "../BandPill/BandPill";
import { CollapsibleSection } from "../CollapsibleSection/CollapsibleSection";
import { CriterionLabel } from "../CriterionLabel/CriterionLabel";

/** Attributed points are fractional averages — one decimal is honest without being noisy. */
const POINTS_FRACTION_DIGITS = 1;

/**
 * The list scrolls past this height instead of growing the panel. A criterion count varies by
 * platform (16 for Meta, 12 for YouTube), and the insights card sits between the report header
 * and the creatives list — letting the row count drive the card height would shift the whole
 * page every time the user switches tabs.
 */
const LIST_MAX_HEIGHT_CLASS = "max-h-60";

function formatPoints(points: number): string {
  return points.toFixed(POINTS_FRACTION_DIGITS);
}

/** `weightPercent` on an attribution row is already a percent — format it without re-scaling. */
function formatWeight(percent: number): string {
  return `${Math.round(percent)}%`;
}

/** Band thresholds as marks on the projection track, so a band flip is visible before it happens. */
function BandThresholdMarks() {
  return (
    <>
      {[SCORE_BAND_NEEDS_REVIEW_MIN, SCORE_BAND_STRONG_MIN].map((threshold) => (
        <span
          key={threshold}
          aria-hidden
          className="bg-background/80 absolute inset-y-0 w-px"
          style={{ left: `${threshold}%` }}
        />
      ))}
    </>
  );
}

/**
 * Where the score stands and where the current selection would take it. The track carries the
 * earned score plus a success-toned extension for the projected gain, so "what fixing this is
 * worth" reads as distance travelled rather than a number to compare by memory.
 */
function ProjectionStrip({
  baseScore,
  projectedScore,
  gained,
  band,
  mounted,
}: {
  baseScore: number;
  projectedScore: number;
  gained: number;
  band: PreflightScoreBand;
  mounted: boolean;
}) {
  const { t } = useTranslation();
  const shownScore = useCountUp(Math.round(projectedScore));
  const hasGain = gained > 0;

  return (
    <div className="flex flex-col gap-2">
      {/* One line, fixed height: the label and the gain both swap on selection, so anything
          that reflowed here would nudge the list below it. */}
      <div className="flex min-h-7 items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-baseline gap-2">
          <span
            className="text-foreground text-2xl leading-none font-semibold tabular-nums"
            aria-hidden
          >
            {shownScore}
          </span>
          <BandPill band={band} />
          <span className="text-muted-foreground truncate text-xs">
            {t(
              hasGain
                ? "preflight.composition.projectedLabel"
                : "preflight.composition.currentLabel",
            )}
          </span>
        </span>
        {hasGain ? (
          <span className="text-success shrink-0 text-sm font-medium tabular-nums">
            {t("preflight.composition.pointsGained", {
              points: formatPoints(gained),
            })}
          </span>
        ) : null}
      </div>

      <div className="bg-muted relative h-1.5 w-full overflow-hidden rounded-full">
        <span
          className="bg-muted-foreground/50 absolute inset-y-0 left-0 transition-[width] duration-500 ease-out motion-reduce:transition-none"
          style={{ width: mounted ? `${baseScore}%` : "0%" }}
        />
        <span
          className="bg-success absolute inset-y-0 transition-[width,left] duration-500 ease-out motion-reduce:transition-none"
          style={{ left: `${baseScore}%`, width: mounted ? `${gained}%` : "0%" }}
        />
        <BandThresholdMarks />
      </div>

      {/* The figure above is aria-hidden so the projection announces as one sentence, not digits. */}
      <p className="sr-only" aria-live="polite">
        {t("preflight.composition.projectionAnnouncement", {
          score: Math.round(projectedScore),
          points: formatPoints(gained),
        })}
      </p>
    </div>
  );
}

function AttributionRow({
  row,
  rank,
  maxPoints,
  selected,
  mounted,
  onToggle,
}: {
  row: CriterionAttribution;
  rank: number;
  maxPoints: number;
  selected: boolean;
  mounted: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const missed = row.failedCount + row.partialCount;
  const width = maxPoints > 0 ? (row.points / maxPoints) * 100 : 0;
  // Split the lost-points bar the way the blockers list does, so fail vs partial composition
  // stays legible. A selected row reads as recovered and collapses to a single success fill.
  const failWidth = missed > 0 ? width * (row.failedCount / missed) : width;
  const partialWidth = missed > 0 ? width * (row.partialCount / missed) : 0;

  return (
    <li>
      <button
        type="button"
        aria-pressed={selected}
        onClick={onToggle}
        className={cn(
          "group flex w-full cursor-pointer flex-col gap-1.5 rounded-md px-2 py-2 text-left",
          "hover:bg-muted/50 focus-visible:ring-ring transition-colors focus-visible:ring-2 focus-visible:outline-none",
          "motion-reduce:transition-none",
        )}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            {/* Hovering previews the tick, so a row reads as selectable before it is clicked. */}
            <span
              aria-hidden
              className={cn(
                "flex size-4 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors motion-reduce:transition-none",
                selected
                  ? "bg-success border-success text-success-foreground"
                  : "border-muted-foreground/40 bg-background text-transparent group-hover:border-muted-foreground/80 group-hover:text-muted-foreground/50",
              )}
            >
              <Icon name="check" size="xs" color="inherit" aria-hidden />
            </span>
            <span
              aria-hidden
              className="text-muted-foreground w-3 shrink-0 text-[11px] font-semibold tabular-nums"
            >
              {rank}
            </span>
            <CriterionLabel
              name={row.name}
              definition={row.definition}
              className="text-foreground min-w-0 text-sm font-medium"
            />
          </span>
          <span className="shrink-0 text-xs tabular-nums">
            <span
              className={cn(
                "font-medium",
                selected ? TONE_TEXT.success : "text-foreground",
              )}
            >
              {t(
                selected
                  ? "preflight.composition.pointsRecovered"
                  : "preflight.composition.pointsLost",
                { points: formatPoints(row.points) },
              )}
            </span>
            {missed > 0 ? (
              <span className="text-muted-foreground">
                {" · "}
                {t("preflight.blockers.missedOf", {
                  count: missed,
                  total: row.scoredAssets,
                })}
              </span>
            ) : null}
            <span className="text-muted-foreground hidden sm:inline">
              {" · "}
              {t("preflight.blockers.weightLabel", {
                weight: formatWeight(row.weightPercent),
              })}
            </span>
          </span>
        </div>
        <div className="bg-muted flex h-2 w-full gap-px overflow-hidden rounded-full">
          {selected ? (
            <span
              className={cn(
                "h-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
                TONE_BG.success,
              )}
              style={{ width: mounted ? `${width}%` : "0%" }}
            />
          ) : (
            <>
              <span
                className={cn(
                  "h-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
                  TONE_BG.error,
                )}
                style={{ width: mounted ? `${failWidth}%` : "0%" }}
              />
              {partialWidth > 0 ? (
                <span
                  className={cn(
                    "h-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
                    TONE_BG.warning,
                  )}
                  style={{ width: mounted ? `${partialWidth}%` : "0%" }}
                />
              ) : null}
            </>
          )}
        </div>
      </button>
    </li>
  );
}

/** The collapsed tail and the unexplained remainder: shown, but not offered as fixable. */
function PassiveRow({
  label,
  points,
  width,
  mounted,
}: {
  label: string;
  points: number;
  width: number;
  mounted: boolean;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-col gap-1.5 px-2 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          {/* Aligns with the checkbox + rank gutter on the selectable rows above. */}
          <span aria-hidden className="w-9 shrink-0" />
          <span className="text-muted-foreground min-w-0 truncate text-sm">
            {label}
          </span>
        </span>
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
          {t("preflight.composition.pointsLost", {
            points: formatPoints(points),
          })}
        </span>
      </div>
      <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
        <span
          className={cn(
            "block h-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
            TONE_BG.muted,
          )}
          style={{ width: mounted ? `${width}%` : "0%" }}
        />
      </div>
    </li>
  );
}

export interface ScoreCompositionProps {
  report: PreflightReportDetail;
  /** Render just the panel body (no collapsible section) — used inside the insights tabs. */
  headless?: boolean;
  className?: string;
}

/**
 * The report's blocking criteria, ranked by the points they cost. This is the single "top
 * blockers" list on a report: the ranking, the points each criterion accounts for, and a what-if
 * in one place, because a rank and a point cost are two readings of the same ordering. Selecting
 * rows projects the score and band, so "is fixing this worth it" is answered on the spot.
 *
 * The market dashboard keeps `ReportTopBlockers` — its aggregate carries miss counts but no
 * per-creative feature scores, so there is nothing there to attribute points from.
 *
 * The points are a proportional attribution of the OBSERVED gap, not DS's own arithmetic (DS
 * emits the score and the weights independently). See `score-attribution.ts` in
 * `@nexus/contracts` — the copy is written to promise no more than that.
 */
export function ScoreComposition({
  report,
  headless = false,
  className,
}: ScoreCompositionProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [mounted, setMounted] = useState(false);

  const attribution = useMemo(
    () => computeScoreAttribution(report),
    [report],
  );

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // A re-scored report can retire a criterion the user had selected.
  useEffect(() => {
    setSelected(new Set());
  }, [attribution]);

  const gained = attribution.rows
    .filter((row) => !row.isTail && selected.has(row.key))
    .reduce((sum, row) => sum + row.points, 0);
  const projection = projectScore(
    attribution.baseScore,
    gained,
    attribution.hasUnanalyzed,
  );
  const ceiling = projectScore(
    attribution.baseScore,
    actionableCeiling(attribution),
    attribution.hasUnanalyzed,
  );
  const maxPoints = Math.max(0, ...attribution.rows.map((row) => row.points));
  const scoredCount = report.scoredCount;

  if (!hasScoreComposition(attribution)) return null;

  const toggle = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const body = (
    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <div className="flex flex-col gap-3">
        <p className="text-muted-foreground text-sm">
          {t("preflight.composition.description", {
            count: scoredCount,
            gap: formatPoints(attribution.observedGap),
          })}
        </p>

        <ProjectionStrip
          baseScore={attribution.baseScore}
          projectedScore={projection.score}
          gained={projection.gained}
          band={projection.band}
          mounted={mounted}
        />

        <ul
          className={cn(
            "-mx-2 flex flex-col overflow-y-auto",
            LIST_MAX_HEIGHT_CLASS,
          )}
        >
          {attribution.rows.map((row, index) =>
            row.isTail ? (
              <PassiveRow
                key={row.key}
                label={t("preflight.composition.tailLabel", {
                  count: row.tailCount,
                })}
                points={row.points}
                width={maxPoints > 0 ? (row.points / maxPoints) * 100 : 0}
                mounted={mounted}
              />
            ) : (
              <AttributionRow
                key={row.key}
                row={row}
                rank={index + 1}
                maxPoints={maxPoints}
                selected={selected.has(row.key)}
                mounted={mounted}
                onToggle={() => toggle(row.key)}
              />
            ),
          )}
          {attribution.unattributedRemainder > 0 ? (
            <PassiveRow
              label={t("preflight.composition.remainderLabel")}
              points={attribution.unattributedRemainder}
              width={
                maxPoints > 0
                  ? (attribution.unattributedRemainder / maxPoints) * 100
                  : 0
              }
              mounted={mounted}
            />
          ) : null}
        </ul>

        {/* Fixed height: the copy and the Clear control both swap on selection, and a footer
            that grows or shrinks would move the panel under the user's cursor. */}
        <div className="flex min-h-8 items-center justify-between gap-3">
          <p className="text-muted-foreground min-w-0 text-xs">
            {selected.size > 0 ? (
              t("preflight.composition.attributionNote")
            ) : (
              <>
                <span className="text-foreground font-medium">
                  {t("preflight.composition.selectHint")}
                </span>
                {" · "}
                {t("preflight.composition.ceilingHint", {
                  score: Math.round(ceiling.score),
                })}
              </>
            )}
          </p>
          {selected.size > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => setSelected(new Set())}
            >
              {t("preflight.composition.clear")}
            </Button>
          ) : null}
        </div>
      </div>
    </TooltipProvider>
  );

  if (headless) return body;

  return (
    <CollapsibleSection
      title={t("preflight.blockers.title")}
      className={className}
    >
      <Card className="gap-0 border-border/60 p-5">{body}</Card>
    </CollapsibleSection>
  );
}
